import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAtomValue, useSetAtom } from 'jotai';
import { AlertTriangle, Check, ChevronDown, ChevronUp, Clock, Loader2, X } from 'lucide-react';
import { STALE_SUGGESTION_CODE } from '@/hooks/useOptimizationReports';
import { clearFinishedJobsAtom, isActive, queueJobsAtom, type QueueJob } from '@/store/optimizationQueue';

const DECISION_LABELS: Record<QueueJob['decision'], string> = {
  approve: 'Aplicar',
  reject: 'Descartar',
  'mark-applied': 'Realizada',
};

/**
 * Progress of the decision queue, pinned to the bottom left of every page (the
 * toasts take the right) while it holds jobs: the trader can leave the
 * execution and still see it.
 */
export function OptimizationQueueIndicator() {
  const jobs = useAtomValue(queueJobsAtom);
  const clearFinished = useSetAtom(clearFinishedJobsAtom);
  const [expanded, setExpanded] = useState(false);

  if (jobs.length === 0) return null;

  const active = jobs.filter(isActive).length;
  const finished = jobs.length - active;
  const problems = jobs.filter((job) => job.status === 'error' || job.recordFailed).length;
  const executions = [...new Set(jobs.map((job) => job.executionId))].join(', ');

  return (
    <div className="fixed bottom-4 left-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] adv-card shadow-lg">
      <div className="flex items-center gap-2 px-3 py-2">
        {active > 0 ? (
          <Loader2 size={16} className="animate-spin text-adv-accent shrink-0" />
        ) : problems > 0 ? (
          <AlertTriangle size={16} className="text-adv-warning-text shrink-0" />
        ) : (
          <Check size={16} className="text-adv-success shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-adv-body-s font-bold truncate">
            {active > 0
              ? `Aplicando cambios ${finished}/${jobs.length}`
              : jobs.length === 1
                ? '1 cambio terminado'
                : `${jobs.length} cambios terminados`}
          </p>
          <p className="text-adv-body-s text-adv-tertiary truncate">
            {executions}
            {problems > 0 && ` · ${problems} con incidencias`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="adv-btn adv-btn--ghost adv-btn--icon adv-btn--sm"
          aria-label={expanded ? 'Ocultar detalle' : 'Ver detalle'}
        >
          {expanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
        </button>
        {active === 0 && (
          <button
            type="button"
            onClick={() => {
              clearFinished();
              setExpanded(false);
            }}
            className="adv-btn adv-btn--ghost adv-btn--icon adv-btn--sm"
            aria-label="Cerrar"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {expanded && (
        <ul className="max-h-[320px] overflow-y-auto border-t border-adv-border divide-y divide-adv-border">
          {jobs.map((job) => (
            <QueueJobRow key={job.id} job={job} />
          ))}
        </ul>
      )}
    </div>
  );
}

function QueueJobRow({ job }: { job: QueueJob }) {
  return (
    <li className="flex items-start gap-2 px-3 py-2 text-adv-body-s">
      <span className="mt-0.5 shrink-0">
        <JobIcon job={job} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate">
          <span className="text-adv-tertiary">{DECISION_LABELS[job.decision]} · </span>
          {job.label}
        </p>
        <Link to={`/campaigns/${job.executionId}`} className="text-adv-tertiary hover:underline adv-num">
          {job.executionId}
        </Link>
        {job.message && (
          <p className="text-adv-warning-text break-words">
            {job.errorCode === STALE_SUGGESTION_CODE ? 'DV360 ha cambiado desde el informe' : job.message}
          </p>
        )}
      </div>
    </li>
  );
}

function JobIcon({ job }: { job: QueueJob }) {
  if (job.status === 'queued') return <Clock size={14} className="text-adv-tertiary" />;
  if (job.status === 'running') return <Loader2 size={14} className="animate-spin text-adv-accent" />;
  if (job.status === 'error' || job.recordFailed) return <AlertTriangle size={14} className="text-adv-warning-text" />;
  return <Check size={14} className="text-adv-success" />;
}
